import { Menu, useMantineColorScheme } from '@mantine/core';
import {
    BookOpen,
    BriefcaseBusiness,
    CalendarCog,
    Cog,
    FileText,
    FolderSync,
    ListIcon,
    Lock,
    LockOpen,
    MessageCircle,
    QrCode,
    RefreshCw,
    Settings,
    SunMoon,
    User,
    UsersRound,
    Workflow,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { AppRoute } from '@/constants/app-routes.ts';

import useAuth from '@/hooks/useAuth.ts';

import { UserEntitlement, UserGroup } from '@/models/User.ts';

import { signOut as appSignOut, copyIdToken } from '@/services/authService.ts';
import { showAppUpdateNotification } from '@/services/notificationService.ts';

import { useConfigStore } from '@/stores/configStore.ts';

import { copyVersion } from '@/utils/envUtils.ts';
import { cn } from '@/utils/styleUtils.ts';

export const UserMenu = () => {
    const { isUserInGroup, picture_url: pictureUrl } = useAuth();
    const navigate = useNavigate();
    const { toggleColorScheme } = useMantineColorScheme();

    const isEngineer: boolean = isUserInGroup([UserGroup.DEV_INTERNAL_ENGINEERS, UserGroup.PROD_INTERNAL_ENGINEERS]);

    const isEngineerOrQa: boolean = isUserInGroup([
        UserGroup.DEV_INTERNAL_ENGINEERS,
        UserGroup.PROD_INTERNAL_ENGINEERS,
        UserGroup.DEV_INTERNAL_QA,
        UserGroup.PROD_INTERNAL_QA,
    ]);

    const isProductManager: boolean = isUserInGroup(UserGroup.INTERNAL_PRODUCT_MANAGER);
    const isHubManager: boolean = isUserInGroup(UserGroup.INTERNAL_HUB_MANAGER);

    const isChatShown: boolean = isUserInGroup([
        UserGroup.DEV_INTERNAL_ENGINEERS,
        UserGroup.PROD_INTERNAL_ENGINEERS,
        UserGroup.INTERNAL_PRODUCT_MANAGER,
    ]);

    const isStaticQrCodeShown: boolean = isUserInGroup([
        UserGroup.DEV_INTERNAL_ENGINEERS,
        UserGroup.PROD_INTERNAL_ENGINEERS,
        UserGroup.INTERNAL_PRODUCT_MANAGER,
        UserGroup.DEV_INTERNAL_QA,
        UserGroup.PROD_INTERNAL_QA,
    ]);

    const isTokenShown: boolean = isUserInGroup(UserGroup.FEATURE_TOKEN);

    const isDcUserManagementShown: boolean = isUserInGroup(UserEntitlement.ENTITLEMENT_DC_USER_MANAGEMENT);

    const signOut = (): void => {
        const isDemoLogin = useConfigStore.getState().is_demo_login;
        appSignOut();
        navigate(isDemoLogin ? AppRoute.DEMO_LOGIN : AppRoute.LOGIN, { replace: true });
    };

    return (
        <Menu trigger="hover">
            <Menu.Target>
                <div
                    data-testid="menu-trigger"
                    className={cn(
                        'overflow-hidden rounded-full border border-slate-300 bg-slate-100',
                        pictureUrl ? 'size-9' : 'p-1.5'
                    )}
                >
                    {pictureUrl ? (
                        <img alt="profile" className="object-cover" src={pictureUrl} />
                    ) : (
                        <User className="size-6 cursor-pointer" />
                    )}
                </div>
            </Menu.Target>

            <Menu.Dropdown>
                {(isEngineer || isTokenShown) && (
                    <Menu.Item onClick={copyIdToken} leftSection={<LockOpen className="size-5" />}>
                        ID Token
                    </Menu.Item>
                )}
                {isEngineer && (
                    <>
                        <Menu.Divider />
                        <Menu.Item
                            onClick={() => navigate(AppRoute.SYNC_REPORTS)}
                            leftSection={<FolderSync className="size-5" />}
                        >
                            Sync Reports
                        </Menu.Item>
                        <Menu.Item
                            onClick={() => navigate(AppRoute.MAPPINGS)}
                            leftSection={<Workflow className="size-5" />}
                        >
                            Mappings
                        </Menu.Item>
                        <Menu.Item
                            onClick={() => navigate(AppRoute.PLAYGROUND)}
                            leftSection={<Cog className="size-5" />}
                        >
                            Playground
                        </Menu.Item>
                        <Menu.Item
                            onClick={() => navigate(AppRoute.LIST_VIEW)}
                            leftSection={<ListIcon className="size-5" />}
                        >
                            ListView
                        </Menu.Item>
                        <Menu.Item onClick={showAppUpdateNotification} leftSection={<RefreshCw className="size-5" />}>
                            Test update prompt
                        </Menu.Item>
                    </>
                )}
                {(isEngineerOrQa || isProductManager) && (
                    <>
                        <Menu.Item
                            onClick={() => navigate(AppRoute.CUSTOMER_MANAGEMENT)}
                            leftSection={<BriefcaseBusiness className="size-5" />}
                        >
                            Customer Management
                        </Menu.Item>
                        <Menu.Item
                            onClick={() => navigate(AppRoute.USER_MANAGEMENT)}
                            leftSection={<User className="size-5" />}
                        >
                            User Management
                        </Menu.Item>
                        <Menu.Item
                            onClick={() => navigate(AppRoute.DATA_CONFIGS)}
                            leftSection={<Settings className="size-5" />}
                        >
                            Data Configs
                        </Menu.Item>
                        <Menu.Item
                            onClick={() => navigate(AppRoute.UNREGISTERED_USER_DOCUMENTS)}
                            leftSection={<FileText className="size-5" />}
                        >
                            Dokumentenaustausch
                        </Menu.Item>
                    </>
                )}
                {(isEngineerOrQa || isProductManager || isHubManager) && (
                    <Menu.Item
                        onClick={() => navigate(AppRoute.CHAINS)}
                        leftSection={<CalendarCog className="size-5" />}
                    >
                        Configure appointment chains
                    </Menu.Item>
                )}
                {isStaticQrCodeShown && (
                    <Menu.Item
                        onClick={() => navigate(AppRoute.STATIC_QR_CODE)}
                        leftSection={<QrCode className="size-5" />}
                    >
                        Static QR Code
                    </Menu.Item>
                )}
                {isChatShown && (
                    <Menu.Item
                        onClick={() => navigate(AppRoute.CHAT)}
                        leftSection={<MessageCircle className="size-5" />}
                    >
                        Chat
                    </Menu.Item>
                )}
                {isEngineer && (
                    <Menu.Item onClick={() => toggleColorScheme()} leftSection={<SunMoon className="size-5" />}>
                        Toggle Theme
                    </Menu.Item>
                )}
                {isDcUserManagementShown && (
                    <Menu.Item
                        onClick={() => navigate(AppRoute.DC_USER_MANAGEMENT)}
                        leftSection={<UsersRound className="size-5" />}
                    >
                        DC User Management
                    </Menu.Item>
                )}
                <Menu.Item onClick={() => navigate(AppRoute.HANDBOOK)} leftSection={<BookOpen className="size-5" />}>
                    Handbuch
                </Menu.Item>
                <Menu.Item onClick={signOut} leftSection={<Lock className="size-5" />}>
                    Ausloggen
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item onClick={copyVersion}>{`Version: ${import.meta.env.VITE_VERSION}`}</Menu.Item>
            </Menu.Dropdown>
        </Menu>
    );
};
